/**
 * R138 · Feb 2026 — Leads Import History
 *
 * Lists every past `leads` import session for the current workspace so
 * admins can audit who brought in which batch, drill into per-row commit
 * results, download the original file, and roll a batch back if needed.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, RotateCcw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function LeadsImportHistory() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/imports`);
      const leadRows = (data || []).filter((r) => r.spec_key === "leads");
      setRows(leadRows);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load history")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (activeWorkspaceId) load(); }, [activeWorkspaceId]); // eslint-disable-line

  const rollback = async (sid) => {
    if (!window.confirm(
      "This will soft-delete every lead created by this import batch. Continue?"
    )) return;
    setRollingBack(sid);
    try {
      const { data } = await api.post(`/imports/${sid}/rollback`);
      toast.success(`Rolled back ${data.rolled_back || 0} leads`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rollback failed")); }
    finally { setRollingBack(null); }
  };

  const summary = (r) => {
    const cr = r.commit_result;
    if (!cr) return "—";
    const total = (cr.committed || []).length;
    const errs = (cr.errors || []).length;
    return `${total} committed · ${errs} errors`;
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 md:px-10" data-testid="leads-import-history-page">
      <PageHeader
        breadcrumb="CRM · Leads"
        title="Lead import history"
        description="Every bulk-import batch that landed in this workspace. Audit who brought in what, and roll back a batch if a mapping went sideways."
        testid="leads-import-history-header"
        actions={
          <Button onClick={() => nav("/app/data-import?entity=leads")}
                  className="rounded-none" data-testid="leads-import-history-new-btn">
            <Upload className="w-4 h-4 mr-2" /> New import
          </Button>
        }
      />

      {busy && rows.length === 0 && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}

      {!busy && rows.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
             data-testid="leads-import-history-empty">
          <Upload className="w-6 h-6 mx-auto mb-2 opacity-60" />
          No lead imports yet. Head to the wizard to upload an Apollo or Aquarius CRM export.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-border overflow-hidden">
          <table className="w-full text-xs" data-testid="leads-import-history-table">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">File</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">Status</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">Rows</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">Result</th>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">Committed at</th>
                <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest text-[9px] text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}
                    className="border-t border-border hover:bg-muted/20"
                    data-testid={`leads-import-history-row-${r.id}`}>
                  <td className="px-3 py-2 truncate max-w-[280px] font-medium"
                      title={r.filename}
                      data-testid={`leads-import-history-file-${r.id}`}>
                    {r.filename || `session ${r.id.slice(0, 8)}`}
                  </td>
                  <td className="px-3 py-2">
                    {r.status === "committed" && r.rollback_at ? (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-widest text-rose-700">
                        <RotateCcw className="w-3 h-3 mr-1" /> Rolled back
                      </span>
                    ) : r.status === "committed" ? (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-widest text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Committed
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-widest text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {r.status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono"
                      data-testid={`leads-import-history-count-${r.id}`}>
                    {r.total_rows ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground"
                      data-testid={`leads-import-history-summary-${r.id}`}>
                    {summary(r)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-[11px]">
                    {r.committed_at ? new Date(r.committed_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status === "committed" && !r.rollback_at && (
                      <button
                        type="button"
                        onClick={() => rollback(r.id)}
                        disabled={rollingBack === r.id}
                        className="text-[11px] text-rose-700 hover:text-rose-900 underline disabled:opacity-40"
                        data-testid={`leads-import-history-rollback-${r.id}`}
                      >
                        {rollingBack === r.id ? "Rolling back…" : "Roll back"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
